import { useState } from 'react';
import { api, can, getUser } from '../lib/api';
import { Attachments } from '../components/Attachments';
import { dateTime, day, number } from '../lib/format';
import {
  Card, Empty, ErrorNote, Field, Hash, Loading, Modal, PageHead, Pill, useResource, useToast,
} from '../components/ui';
import { RowActions } from '../components/RowActions';
import '../styles/children.css';

/**
 * S.9 DPDP Act with Rules 10 and 11: consent for a child, or for a person with
 * disability, held until the parent or lawful guardian has confirmed by code
 * and their identity has been checked.
 */

type Status = 'awaiting_guardian' | 'awaiting_check' | 'verified' | 'rejected' | 'expired';
type Kind = 'child' | 'disability';

interface GuardianConsent {
  id: string;
  collectionPointId: string;
  collectionPoint: string;
  kind: Kind;
  childHash: string;
  guardianHash: string;
  relationship: 'parent' | 'lawful_guardian';
  guardianChannel: 'email' | 'sms';
  guardianContact: string;
  status: Status;
  decisions: Array<{ purposeId: string; purpose: string; granted: boolean }>;
  contactVerifiedAt: string | null;
  declaration: { isAdult?: boolean; isParentOrGuardian?: boolean; at?: string } | null;
  checkMethod: string | null;
  checkNote: string;
  checkedBy: string | null;
  checkedAt: string | null;
  rejectedReason: string | null;
  recordedEvents: number;
  createdAt: string;
  expiresAt: string | null;
}

interface Summary { awaitingGuardian: number; awaitingCheck: number; verified: number; rejected: number; children: number }
interface CheckMethod { key: string; label: string; rule: string; kinds: Kind[] }

const STATUS_LABEL: Record<Status, string> = {
  awaiting_check: 'Waiting for check',
  awaiting_guardian: 'Waiting for guardian',
  verified: 'Verified',
  rejected: 'Rejected',
  expired: 'Expired',
};
const STATUS_TONE: Record<Status, 'ok' | 'warn' | 'bad' | undefined> = {
  awaiting_check: 'warn', awaiting_guardian: undefined, verified: 'ok', rejected: 'bad', expired: undefined,
};
const TABS: Status[] = ['awaiting_check', 'awaiting_guardian', 'verified', 'rejected', 'expired'];
const COUNT_KEY: Partial<Record<Status, keyof Summary>> = {
  awaiting_check: 'awaitingCheck', awaiting_guardian: 'awaitingGuardian', verified: 'verified', rejected: 'rejected',
};

const KIND_LABEL: Record<Kind, string> = { child: 'Child', disability: 'Person with disability' };
const RELATIONSHIP_LABEL = { parent: 'Parent', lawful_guardian: 'Lawful guardian' } as const;

const choices = (d: GuardianConsent['decisions']) => {
  const yes = d.filter((x) => x.granted).length;
  const no = d.length - yes;
  return [yes ? `${yes} allowed` : '', no ? `${no} refused` : ''].filter(Boolean).join(', ') || '—';
};

export default function GuardianConsents() {
  const canCheck = can(getUser(), 'principals:guardian');
  const [status, setStatus] = useState<Status>('awaiting_check');
  const [reload, setReload] = useState(0);
  const [open, setOpen] = useState<GuardianConsent | null>(null);
  const [rejecting, setRejecting] = useState<GuardianConsent | null>(null);

  const data = useResource<{ guardianConsents: GuardianConsent[]; summary: Summary }>(
    () => api.get(`/v1/guardian-consents?status=${status}`), [status, reload],
  );
  const methods = useResource<{ methods: CheckMethod[] }>(() => api.get('/v1/guardian-check-methods'));
  const rows = data.data?.guardianConsents ?? [];
  const summary = data.data?.summary;
  const refresh = () => setReload((n) => n + 1);

  return (
    <>
      <PageHead title="Children and guardians" />

      {summary && (
        <p className="page-line">
          <strong>{number(summary.children)}</strong> people recorded as children or under guardianship · {number(summary.verified)} verified · {number(summary.awaitingCheck)} waiting for check
        </p>
      )}

      <ErrorNote error={data.error} />

      <div className="tabs">
        {TABS.map((t) => {
          const key = COUNT_KEY[t];
          const count = key && summary ? summary[key] : 0;
          return (
            <button key={t} className={status === t ? 'active' : ''} onClick={() => setStatus(t)}>
              {STATUS_LABEL[t]}
              {count ? <span className="tab-count">{count}</span> : null}
            </button>
          );
        })}
      </div>

      <Card>
        {data.loading && !data.data && <Loading what="Loading guardian consents" />}
        {data.data && rows.length === 0 && <Empty>None.</Empty>}
        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Received</th><th>For</th><th>Guardian</th><th>Collection point</th><th>Choices</th><th>Status</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => setOpen(r)} style={{ cursor: 'pointer' }}>
                    <td className="small">{day(r.createdAt)}</td>
                    <td>{KIND_LABEL[r.kind]}</td>
                    <td className="text-list">{RELATIONSHIP_LABEL[r.relationship]} · <span className="mono">{r.guardianContact}</span></td>
                    <td className="text-list">{r.collectionPoint}</td>
                    <td className="small">{choices(r.decisions)}</td>
                    <td><Pill tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Pill></td>
                    <td className="u-actions" onClick={(e) => e.stopPropagation()}>
                      <RowActions
                        primary={canCheck && r.status === 'awaiting_check'
                          ? <button className="small primary" onClick={() => setOpen(r)}>Record check</button>
                          : undefined}
                        more={[
                          ...(canCheck && (r.status === 'awaiting_check' || r.status === 'awaiting_guardian')
                            ? [{ label: 'Reject', danger: true, onClick: () => setRejecting(r) }] : []),
                          { label: 'View', onClick: () => setOpen(r) },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open && (
        <GuardianDetail
          row={open}
          methods={methods.data?.methods ?? []}
          canCheck={canCheck}
          onClose={() => setOpen(null)}
          onChecked={(updated) => { setOpen(updated); refresh(); }}
          onReject={() => { setRejecting(open); setOpen(null); }}
        />
      )}

      {rejecting && (
        <RejectDialog
          row={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => { setRejecting(null); refresh(); }}
        />
      )}
    </>
  );
}

function GuardianDetail({ row: r, methods, canCheck, onClose, onChecked, onReject }: {
  row: GuardianConsent;
  methods: CheckMethod[];
  canCheck: boolean;
  onClose: () => void;
  onChecked: (row: GuardianConsent) => void;
  onReject: () => void;
}) {
  const toast = useToast();
  const applicable = methods.filter((m) => m.kinds.includes(r.kind));
  const [method, setMethod] = useState<string>('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const chosen = method || applicable[0]?.key || '';
  const needsReference = chosen === 'token' || r.kind === 'disability';
  const checkable = canCheck && r.status === 'awaiting_check';
  const used = methods.find((m) => m.key === r.checkMethod);

  async function record() {
    setBusy(true);
    try {
      const res = await api.post<{ guardianConsent: GuardianConsent }>(`/v1/guardian-consents/${r.id}/check`, {
        method: chosen,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast(`Check recorded. ${res.guardianConsent.recordedEvents} events recorded.`, 'ok');
      onChecked(res.guardianConsent);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      wide
      title={`${KIND_LABEL[r.kind]} · ${r.collectionPoint}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Close</button>
          {checkable && <button className="danger" onClick={onReject} disabled={busy}>Reject</button>}
          {checkable && (
            <button className="primary" onClick={record} disabled={busy || !chosen || (needsReference && !reference.trim())}>
              {busy ? 'Recording…' : 'Record check'}
            </button>
          )}
        </>
      }
    >
      <dl className="kv">
        <dt>Status</dt><dd><Pill tone={STATUS_TONE[r.status]}>{STATUS_LABEL[r.status]}</Pill></dd>
        <dt>Received</dt><dd>{dateTime(r.createdAt)}</dd>
        {(r.status === 'awaiting_guardian' || r.status === 'awaiting_check') && r.expiresAt && (
          <><dt>Expires</dt><dd>{day(r.expiresAt)}</dd></>
        )}
        <dt>For</dt><dd>{KIND_LABEL[r.kind]} · record <Hash value={r.childHash} head={10} /></dd>
        <dt>Guardian</dt>
        <dd>
          {RELATIONSHIP_LABEL[r.relationship]} · {r.guardianChannel === 'email' ? 'Email' : 'SMS'} <span className="mono">{r.guardianContact}</span> · record <Hash value={r.guardianHash} head={10} />
        </dd>
        <dt>Declaration</dt>
        <dd>
          {r.contactVerifiedAt
            ? <>Confirmed by code on {day(r.contactVerifiedAt)}{r.declaration?.isAdult && r.declaration?.isParentOrGuardian ? ' · aged 18 or over, parent or lawful guardian' : ''}</>
            : <span className="muted">Not confirmed</span>}
        </dd>
      </dl>

      <h2 className="section">{r.status === 'verified' ? 'Choices recorded' : 'Choices held'}</h2>
      {r.decisions.length === 0 ? <Empty>No choices.</Empty> : (
        <table className="data">
          <thead><tr><th>Purpose</th><th>Choice</th></tr></thead>
          <tbody>
            {r.decisions.map((d) => (
              <tr key={d.purposeId}>
                <td>{d.purpose}</td>
                <td className={d.granted ? '' : 'gc-choice-no'}>{d.granted ? 'Allowed' : 'Refused'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {r.status === 'verified' && (
        <>
          <h2 className="section">Check</h2>
          <dl className="kv">
            <dt>Method</dt><dd>{used?.label ?? r.checkMethod ?? '—'}</dd>
            <dt>Rule</dt><dd>{used?.rule ?? '—'}</dd>
            <dt>Checked by</dt><dd>{r.checkedBy ?? '—'}</dd>
            <dt>Checked</dt><dd>{dateTime(r.checkedAt)}</dd>
            <dt>Events recorded</dt><dd>{r.recordedEvents}</dd>
            {r.checkNote && <><dt>Note</dt><dd>{r.checkNote}</dd></>}
          </dl>
        </>
      )}

      {r.status === 'rejected' && (
        <>
          <h2 className="section">Rejection</h2>
          <dl className="kv">
            <dt>Reason</dt><dd>{r.rejectedReason ?? '—'}</dd>
            {r.checkedBy && <><dt>Rejected by</dt><dd>{r.checkedBy}</dd></>}
            {r.checkedAt && <><dt>Rejected</dt><dd>{dateTime(r.checkedAt)}</dd></>}
          </dl>
        </>
      )}

      {checkable && (
        <>
          <h2 className="section">{r.kind === 'child' ? 'Check under Rule 10' : 'Check under Rule 11'}</h2>
          <Field label="Method">
            <div className="gc-methods" role="radiogroup">
              {applicable.map((m) => (
                <label key={m.key}>
                  <input type="radio" name="gc-method" value={m.key} checked={chosen === m.key} onChange={() => setMethod(m.key)} />
                  <span>{m.label}<span className="gc-rule">{m.rule}</span></span>
                </label>
              ))}
            </div>
          </Field>
          <Field label={needsReference ? 'Reference (stored as a hash)' : 'Reference (stored as a hash, optional)'}>
            <input type="text" value={reference} autoComplete="off" onChange={(e) => setReference(e.target.value)} />
          </Field>
          <Field label="Note (optional)">
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </>
      )}

      <h2 className="section">Evidence files</h2>
      <Attachments entityType="guardian" entityId={r.id} canAdd={canCheck && (r.status === 'awaiting_check' || r.status === 'verified')} />
    </Modal>
  );
}

function RejectDialog({ row, onClose, onDone }: { row: GuardianConsent; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function reject() {
    setBusy(true);
    try {
      await api.post(`/v1/guardian-consents/${row.id}/reject`, { reason: reason.trim() });
      toast('Rejected. Nothing recorded for the child.', 'ok');
      onDone();
    } catch (err) {
      toast((err as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Reject · ${KIND_LABEL[row.kind]} · ${row.collectionPoint}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>Cancel</button>
          <button className="danger" onClick={reject} disabled={busy || !reason.trim()}>{busy ? 'Rejecting…' : 'Reject'}</button>
        </>
      }
    >
      <Field label="Reason">
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}

/* ------------------------------------------------------------ evidence files */
